import React, { useState, useEffect } from "react";
import { Button } from "../../components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, methodologyService, DEFAULT_METHODOLOGIES, Methodology, GuidedMethodology } from "../../lib/firebase";
import { useSystemSettings } from "../../lib/SystemSettingsContext";
import { Alert, AlertTitle, AlertDescription } from "../../components/ui/alert";
import SystemSettingsChecker from "../../components/ui/SystemSettingsChecker";
import FeaturedMethodology from "../../components/ui/FeaturedMethodology";
import FeaturedToolkit from "../../components/ui/FeaturedToolkit";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { redisService } from '../../lib/redisService';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Search, Play, BookOpen, Shield, Users, Settings, LogOut, TrendingUp, Clock, Zap } from 'lucide-react';

interface MenuItem {
  title: string;
  delay: number;
  path: string;
  icon?: string;
}

interface LastSession {
  methodologyId: string;
  subMethodologyId?: string;
  lastAccessed: string;
  title: string;
  type?: 'straight-up' | 'guided';
}

interface SearchResult {
  id: string;
  title: string;
  description?: string;
  type: 'straight-up' | 'guided';
  path: string;
}

export const DashboardPage = (): JSX.Element => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [userName, setUserName] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [methodologies, setMethodologies] = useState<Methodology[]>([]);
  const [guidedMethodologies, setGuidedMethodologies] = useState<GuidedMethodology[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastSession, setLastSession] = useState<LastSession | null>(null);
  const [greeting, setGreeting] = useState<string>("Welcome back");
  const navigate = useNavigate();
  const { settings, loading: settingsLoading } = useSystemSettings();
  
  // Search functionality
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [performanceMetrics, setPerformanceMetrics] = useState<any>(null);
  const [showPerformanceMetrics, setShowPerformanceMetrics] = useState(false);

  // Authentication effect
  useEffect(() => {
    if (!auth) {
      setError("Authentication service unavailable");
      setLoading(false);
      setSessionLoading(false);
      return () => {};
    }

    // Check if we have a stored username first
    const storedName = localStorage.getItem('rawpa_user_name');
    if (storedName) {
      setUserName(storedName);
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        console.log(`User authenticated: ${user.uid}, email: ${user.email}`);
        
        // Only set username from auth if we don't already have one in localStorage
        if (!storedName) {
          const displayName = user.displayName || user.email?.split('@')[0] || 'User';
          setUserName(displayName);
          localStorage.setItem('rawpa_user_name', displayName);
        }
        
        // Set initial greeting immediately - will work for both new and returning users
        const initialGreetings = [
          "Welcome to RAWPA",
          "Your security workspace is ready",
          "Ready to continue your journey",
          "Great to see you"
        ];
        const randomIndex = Math.floor(Math.random() * initialGreetings.length);
        setGreeting(initialGreetings[randomIndex]);
        
        // Wait for system settings to load before showing UI
        if (!settingsLoading) {
          setLoading(false);
        }
        
        // Run non-blocking operations in parallel
        Promise.allSettled([
          // Sync user progress in background (non-blocking)
          methodologyService.ensureUserProgressSync().catch(error => {
            console.error("Error syncing user progress:", error);
          }),
          
          // Check for last session in background (non-blocking)
          checkForLastSession(user.uid).catch(error => {
            console.error("Error checking last session:", error);
            setSessionLoading(false);
          })
        ]);
      } else {
        console.log("User not authenticated, redirecting to signin");
        navigate('/signin');
        setSessionLoading(false);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, settingsLoading]);

  // Update loading state when settings are loaded
  useEffect(() => {
    if (!settingsLoading && !loading) {
      setLoading(false);
    }
  }, [settingsLoading, loading]);

  // Update performance metrics periodically
  useEffect(() => {
    updatePerformanceMetrics();
    const interval = setInterval(updatePerformanceMetrics, 10000); // Update every 10 seconds instead of 5
    return () => clearInterval(interval);
  }, []);

  // Load guided methodologies for the menu (non-blocking)
  useEffect(() => {
    // Skip if guided methodologies are disabled
    if (settings.disableGuidedMethodologies) {
      return;
    }
    
    const loadGuidedMethodologies = async () => {
      try {
        const data = await methodologyService.getGuidedMethodologies();
        setGuidedMethodologies(data.filter(m => m.status === 'Active'));
      } catch (error) {
        console.error("Error loading guided methodologies:", error);
      }
    };

    // Load in background without blocking UI
    loadGuidedMethodologies();
  }, [settings.disableGuidedMethodologies]);

  // Check if user has any completed content items (optimized)
  const checkForLastSession = async (userId: string) => {
    try {
      console.log(`Checking for last session for user ${userId}`);
      
      // Ensure user is still authenticated
      if (!auth || !auth.currentUser || auth.currentUser.uid !== userId) {
        console.warn("User authentication state changed during session check");
        setSessionLoading(false);
        return;
      }
      
      // Set a shorter timeout to prevent UI blocking
      const sessionCheckTimeout = setTimeout(() => {
        console.log("Session check taking longer than expected, showing UI anyway");
        setSessionLoading(false);
      }, 1500); // Reduced from 3 seconds to 1.5 seconds
      
      // Run operations in parallel for better performance
      const [cleanupResult, methodologiesResult] = await Promise.allSettled([
        methodologyService.cleanupInvalidSessions(),
        methodologyService.findMethodologiesWithCompletedItems()
      ]);
      
      // Clear the timeout since we got a result
      clearTimeout(sessionCheckTimeout);
      
      if (methodologiesResult.status === 'fulfilled' && methodologiesResult.value) {
        const methodologiesWithCompletedItems = methodologiesResult.value;
        console.log(`Last session found - Type: ${methodologiesWithCompletedItems.type || 'straight-up'}, ID: ${methodologiesWithCompletedItems.methodologyId}`);
        
        // Check if the methodology type is disabled
        if ((methodologiesWithCompletedItems.type === 'guided' && settings.disableGuidedMethodologies) ||
            (methodologiesWithCompletedItems.type === 'straight-up' && settings.disableStraightUpMethodologies) ||
            (!methodologiesWithCompletedItems.type && settings.disableStraightUpMethodologies)) {
          // Skip setting last session if the methodology type is disabled
          setSessionLoading(false);
          return;
        }
        
        // Set the session with the correct type
        setLastSession({
          ...methodologiesWithCompletedItems,
          // Only use default 'straight-up' if type is undefined
          type: methodologiesWithCompletedItems.type || 'straight-up'
        });
        
        // Don't change the greeting - keep the initial one set during authentication
        console.log("Found existing session - keeping initial greeting");
      } else {
        console.log("No last session found - keeping initial new user greeting");
        // Don't change the greeting for new users - keep the initial one set above
      }
      
      // Mark session loading as complete
      setSessionLoading(false);
    } catch (error) {
      console.error("Error checking for last session:", error);
      setSessionLoading(false);
    }
  };

  // Load methodologies (optimized with better error handling)
  useEffect(() => {
    console.group('DashboardPage - Methodology Subscription');
    console.log("Setting up methodologies subscription in DashboardPage");
    
    // Don't set loading here - let the auth effect handle it
    const unsubscribe = methodologyService.subscribeToMethodologies((updatedMethodologies) => {
      console.log("Dashboard received updated methodologies count:", updatedMethodologies.length);
      
      // Check for sub-methodologies
      let subMethodologiesCount = 0;
      updatedMethodologies.forEach(m => {
        if (m.subMethodologies && m.subMethodologies.length > 0) {
          console.log(`Methodology ${m.id} (${m.title}) has ${m.subMethodologies.length} sub-methodologies:`, 
            m.subMethodologies.map(sm => sm.title).join(', '));
          subMethodologiesCount += m.subMethodologies.length;
        }
      });
      
      console.log(`Total sub-methodologies: ${subMethodologiesCount}`);
      setMethodologies(updatedMethodologies);
    });

    return () => {
      console.log("Cleaning up methodologies subscription in DashboardPage");
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
      console.groupEnd();
    };
  }, []);

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      } else {
        setError("Authentication service unavailable");
      }
    } catch (error) {
      console.error('Error signing out:', error);
      setError("Failed to sign out. Please try again.");
    }
  };

  const handleContinueSession = async () => {
    if (lastSession) {
      try {
        console.log(`Continuing session - Type: ${lastSession.type}, ID: ${lastSession.methodologyId}, SubID: ${lastSession.subMethodologyId || 'N/A'}`);
        
        // Check if the methodology type is disabled
        if ((lastSession.type === 'guided' && settings.disableGuidedMethodologies) ||
            (lastSession.type === 'straight-up' && settings.disableStraightUpMethodologies)) {
          setError(`${lastSession.type === 'guided' ? 'Guided' : 'Straight-up'} methodologies are currently disabled by the administrator.`);
          return;
        }
        
        // Verify the methodology exists before navigating
        if (lastSession.type === 'guided') {
          console.log("Continuing guided methodology session");
          // Check if guided methodology exists
          const guidedMethodology = await methodologyService.getGuidedMethodologyById(lastSession.methodologyId);
          if (!guidedMethodology) {
            setError("The previous session's methodology is no longer available");
            return;
          }
          navigate(`/guided-methodologies/${lastSession.methodologyId}`);
        } else {
          console.log("Continuing straight-up methodology session");
          // Check if straight-up methodology exists
          const methodology = await methodologyService.getMethodologyById(lastSession.methodologyId);
          if (!methodology) {
            setError("The previous session's methodology is no longer available");
            return;
          }
          
          if (lastSession.subMethodologyId) {
            navigate(`/methodology/${lastSession.methodologyId}/${lastSession.subMethodologyId}`);
          } else {
            navigate(`/methodology/${lastSession.methodologyId}`);
          }
        }
      } catch (error) {
        console.error("Error continuing session:", error);
        setError("Failed to continue your session. Please try again.");
      }
    }
  };

  // Update performance metrics
  const updatePerformanceMetrics = async () => {
    const metrics = redisService.getPerformanceMetrics() as any;
    
    // Test Redis connection if available
    if (redisService.isAvailable()) {
      const connectionTest = await redisService.testConnection();
      metrics.connectionTest = connectionTest;
    }
    
    setPerformanceMetrics(metrics);
  };

  // Handle search
  const handleSearch = (query: string): void => {
    setSearchQuery(query);
    
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    
    const lowerQuery = query.toLowerCase();
    
    // Filter straight-up methodologies (exclude any that might be disabled)
    const straightUpResults = !settings.disableStraightUpMethodologies 
      ? methodologies
          .filter(m => 
            // Filter out pathway if disabled
            !(m.id === 'pathway' && settings.disablePathway) &&
            // Search in title, description, or keywords
            (m.title.toLowerCase().includes(lowerQuery) || 
             (m.description && m.description.toLowerCase().includes(lowerQuery)) ||
             (m.keywords && m.keywords.some(k => k.toLowerCase().includes(lowerQuery))))
          )
          .map(m => ({
            id: m.id,
            title: m.title,
            description: m.description,
            type: 'straight-up' as const,
            path: `/methodology/${m.id}`
          }))
      : [];
    
    // Filter guided methodologies (exclude drafts, inactive, or if disabled)
    const guidedResults = !settings.disableGuidedMethodologies
      ? guidedMethodologies
          .filter(m => 
            // Only include active methodologies (filter out drafts and inactive)
            m.status === 'Active' &&
            // Search in name, description, or keywords
            (m.name.toLowerCase().includes(lowerQuery) || 
             (m.description && m.description.toLowerCase().includes(lowerQuery)) ||
             (m.keywords && m.keywords.some(k => k.toLowerCase().includes(lowerQuery))))
          )
          .map(m => ({
            id: m.id,
            title: m.name,
            description: m.description,
            type: 'guided' as const,
            path: `/guided-methodologies/${m.id}`
          }))
      : [];
    
    // Combine results
    setSearchResults([...straightUpResults, ...guidedResults]);
  };

  // Check if all methodologies are disabled
  const areAllMethodologiesDisabled = 
    settings.disableStraightUpMethodologies && 
    settings.disableGuidedMethodologies && 
    settings.disablePathway;

  // Show loading state only if both loading and sessionLoading are true
  const isLoading = loading || sessionLoading || settingsLoading;

  // Show the main content even while session is loading
  if (loading || settingsLoading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  // Create menu items from methodologies
  const menuItems: MenuItem[] = [];
  
  // Add straight-up methodologies if not disabled
  if (!settings.disableStraightUpMethodologies) {
    methodologies.forEach((methodology, index) => {
      // Skip "pathway" methodology if pathway is disabled
      if (methodology.id === "pathway" && settings.disablePathway) {
        return;
      }
      const path = `/methodology/${methodology.id}?newSession=true`;
      menuItems.push({
        title: methodology.title,
        delay: index * 0.1,
        path
      });
    });
  }
  
  // Add guided methodologies if not disabled
  if (!settings.disableGuidedMethodologies) {
    guidedMethodologies
      .filter(methodology => methodology.name !== "Basic Web Application Assessment")
      .forEach((methodology, index) => {
        const path = `/guided-methodologies/${methodology.id}?newSession=true`;
        menuItems.push({
          title: `Guided: ${methodology.name}`,
          delay: (methodologies.length + index) * 0.1,
          path
        });
      });
  }
  
  // Add RAWPA AI option if not disabled
  if (!settings.disableAI) {
    menuItems.push({ 
      title: "Ask RAWPA AI",
      delay: (methodologies.length + guidedMethodologies.length) * 0.1,
      icon: "/rawpa-ai-icon.svg",
      path: '/rawpa-ai'
    });
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4">
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA</span>
            </div>
            <div className="flex items-center gap-3">
              <div 
                onClick={() => setIsSearchOpen(true)}
                className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
            </div>
            <div 
              onClick={handleSignOut}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="User" src="/user-icon.svg" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px]">
        {error && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}
        
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          <div className="mb-8">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              {greeting}, {userName}!
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Quick Actions/Start New
            </p>
          </div>
          
          {areAllMethodologiesDisabled ? (
            <Alert className="mb-4 bg-blue-50 border-blue-200">
              <AlertTitle className="text-blue-800 font-semibold">All Methodologies Unavailable</AlertTitle>
              <AlertDescription className="text-blue-700">
                All methodologies are currently undergoing enhancements. Please check back later for updates.
              </AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-4">
              {lastSession && (
                <Button 
                  className="w-full h-12 bg-[#daed6e] rounded-[20px] hover:bg-[#c5d85c] transition-colors flex items-center justify-center gap-2"
                  onClick={handleContinueSession}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                    Continue Previous Session
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                  </svg>
                </Button>
              )}
              
              {/* Subtle loading indicator for session check */}
              {sessionLoading && !lastSession && (
                <div className="w-full h-12 bg-gray-100 rounded-[20px] flex items-center justify-center gap-2 opacity-60">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                  <span className="font-['Space_Grotesk'] text-sm text-gray-600">
                    Checking for previous sessions...
                  </span>
                </div>
              )}
              
              <Button 
                className="w-full h-12 bg-[#daed6e] rounded-[20px] hover:bg-[#c5d85c] transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                disabled={menuItems.length === 0}
              >
                <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                  Start New Session
                </span>
              </Button>
              
              {/* AnimatePresence for menu items - positioned above Featured Methodology */}
              <AnimatePresence>
                {isMenuOpen && (
                  <div className="space-y-2 mt-4">
                    {menuItems.map((item) => (
                      <motion.button
                        key={item.title}
                        initial={{ x: 200, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        exit={{ x: -200, opacity: 0 }}
                        transition={{ 
                          duration: 0.3,
                          delay: item.delay,
                          ease: "easeOut"
                        }}
                        onClick={() => navigate(item.path)}
                        className="w-full h-12 bg-white rounded-[20px] flex items-center justify-center gap-2 shadow-md hover:bg-gray-50 transition-colors border border-[#E7E7E7]"
                      >
                        <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                          {item.title}
                        </span>
                        {item.icon && <img src={item.icon} alt="RAWPA AI" className="w-5 h-5" />}
                      </motion.button>
                    ))}
                  </div>
                )}
              </AnimatePresence>
              
              {/* RAWPA AI Button - only show if AI is not disabled */}
              {!settings.disableAI && (
                <Button 
                  className="w-full h-12 bg-white rounded-[20px] border border-[#daed6e] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  onClick={() => navigate('/rawpa-ai')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-base">
                    Ask RAWPA AI Assistant
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                    <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                  </svg>
                </Button>
              )}
              
              {/* Pentest Orchestrator Button - show independently of AI setting */}
              {!settings.disablePentestOrchestrator && (
                <Button 
                  className="w-full h-12 bg-gradient-to-r from-purple-500 to-blue-600 rounded-[20px] hover:from-purple-600 hover:to-blue-700 transition-all text-white flex items-center justify-center gap-2 shadow-lg"
                  onClick={() => navigate('/pentest-orchestrator')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-white text-base">
                  Pentest Orchestrator
                  </span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </Button>
              )}
              
              {/* The Toolkits Button */}
              <Button 
                className="w-full h-12 bg-white rounded-[20px] border border-[#daed6e] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                onClick={() => navigate('/toolkits')}
              >
                <span className="font-['Space_Grotesk'] font-medium text-black text-base">The Toolkits
                </span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </Button>
              
              {/* Hunter's Board Button */}
              {!settings.disableHuntersBoard && (
                <Button
                  className="w-full h-12 bg-white rounded-[20px] border border-[#daed6e] hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
                  onClick={() => navigate('/hunters-board')}
                >
                  <span className="font-['Space_Grotesk'] font-medium text-black text-base">Hunter's Board
                  </span>
                </Button>
              )}
              
              {/* Featured Methodology Section */}
              {settings.featuredMethodology && (
                <div className="mt-6 mb-2">
                  <h2 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
                    Featured Methodology
                  </h2>
                  <FeaturedMethodology
                    id={settings.featuredMethodology.id}
                    title={settings.featuredMethodology.title}
                    type={settings.featuredMethodology.type}
                    description={settings.featuredMethodology.description}
                  />
                </div>
              )}

              {/* Featured Toolkit Section */}
              {settings.featuredToolkit && (
                <div className="mt-6 mb-2">
                  <h2 className="font-['Space_Grotesk'] font-medium text-base text-black mb-2">
                    Featured Toolkit
                  </h2>
                  <FeaturedToolkit
                    id={settings.featuredToolkit.id}
                    title={settings.featuredToolkit.title}
                    type={settings.featuredToolkit.type}
                    description={settings.featuredToolkit.description}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      
      {/* Performance Metrics Display */}
      {showPerformanceMetrics && performanceMetrics && (
        <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-4 max-w-sm">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm text-gray-800">Cache Performance</h3>
            <Button
              size="sm"
              variant="outline"
              onClick={updatePerformanceMetrics}
              className="h-6 px-2 text-xs"
            >
              <Zap className="h-3 w-3 mr-1" />
              Refresh
            </Button>
          </div>
          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-600">Cache Hit Rate:</span>
              <span className="font-medium">{performanceMetrics.cacheHitRate}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Total Requests:</span>
              <span className="font-medium">{performanceMetrics.totalRequests}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Avg Response:</span>
              <span className="font-medium">{performanceMetrics.averageResponseTime.toFixed(1)}ms</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Storage:</span>
              <span className="font-medium">{redisService.isAvailable() ? 'Redis' : 'localStorage'}</span>
            </div>
            {performanceMetrics.connectionTest !== undefined && (
              <div className="flex justify-between">
                <span className="text-gray-600">Connection:</span>
                <span className={`font-medium ${performanceMetrics.connectionTest ? 'text-green-600' : 'text-red-600'}`}>
                  {performanceMetrics.connectionTest ? 'Connected' : 'Failed'}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Performance Metrics Toggle Button */}
      <Button
        size="sm"
        variant="outline"
        onClick={() => setShowPerformanceMetrics(!showPerformanceMetrics)}
        className="fixed bottom-4 left-4 h-8 px-3 text-xs bg-white shadow-lg border border-gray-200"
      >
        <TrendingUp className="h-3 w-3 mr-1" />
        {showPerformanceMetrics ? 'Hide' : 'Show'} Performance
      </Button>
      
      {/* Search Dialog */}
      <Dialog open={isSearchOpen} onOpenChange={setIsSearchOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Search Methodologies</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Search by title, description, or keywords..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="mb-4"
              autoFocus
            />
            
            {searchQuery && searchResults.length === 0 ? (
              <div className="text-center py-4 text-gray-500">
                No methodologies found matching your search.
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {searchResults.map((result) => (
                  <div
                    key={`${result.type}-${result.id}`}
                    className="p-3 border rounded-md cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      navigate(result.path);
                      setIsSearchOpen(false);
                    }}
                  >
                    <div className="flex justify-between">
                      <h3 className="font-medium">{result.title}</h3>
                      <Badge variant={result.type === 'guided' ? 'outline' : 'secondary'}>
                        {result.type === 'guided' ? 'Guided' : 'Straight-up'}
                      </Badge>
                    </div>
                    {result.description && (
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">{result.description}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
